/**
 * Утилиты для управления вибрацией в Telegram Web App и PWA/браузере.
 * Стратегия best-effort: пытаемся использовать доступный канал, без ошибок на неподдерживаемых платформах.
 */

export interface VibrationPattern {
  pattern: number | readonly number[]
  telegramStyle?: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'
  telegramType?: 'error' | 'success' | 'warning'
  description: string
}

// Предустановленные паттерны вибрации
export const VIBRATION_PATTERNS = {
  button: {
    pattern: 50 as const,
    telegramStyle: 'light' as const,
    description: 'Короткая вибрация для кнопок'
  },

  success: {
    pattern: [100, 50, 100] as const,
    telegramType: 'success' as const,
    description: 'Двойная вибрация для успешных действий'
  },

  error: {
    pattern: 200 as const,
    telegramType: 'error' as const,
    description: 'Длинная вибрация для ошибок'
  },

  critical: {
    pattern: [50, 50, 50, 50, 50] as const,
    telegramStyle: 'heavy' as const,
    description: 'Тройная вибрация для критических действий'
  }
}

/**
 * Проверяет, является ли приложение Telegram Web App
 */
export const isTelegramWebApp = (): boolean => {
  return !!(window.Telegram?.WebApp)
}

/**
 * Проверяет доступность Telegram HapticFeedback API
 */
export const isTelegramHapticSupported = (): boolean => {
  return !!window.Telegram?.WebApp?.HapticFeedback
}

/**
 * Проверяет доступность Web Vibration API
 */
export const isWebVibrationSupported = (): boolean => {
  return typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function'
}

/**
 * Проверяет, доступен ли хотя бы один канал вибрации
 */
export const isVibrationSupported = (): boolean => {
  return isTelegramHapticSupported() || isWebVibrationSupported()
}

/**
 * Текущий канал, через который будет отправлена вибрация
 */
export const getVibrationChannel = (): 'telegram' | 'web' | 'none' => {
  if (isTelegramHapticSupported()) {
    return 'telegram'
  }

  if (isWebVibrationSupported()) {
    return 'web'
  }

  return 'none'
}

/**
 * Вызывает вибрацию через Telegram HapticFeedback
 */
const vibrateTelegram = (pattern: VibrationPattern): boolean => {
  try {
    const haptic = window.Telegram?.WebApp?.HapticFeedback
    if (!haptic) {
      return false
    }

    if (pattern.telegramType) {
      haptic.notificationOccurred(pattern.telegramType)
      return true
    }

    if (pattern.telegramStyle) {
      haptic.impactOccurred(pattern.telegramStyle)
      return true
    }

    haptic.selectionChanged()
    return true
  } catch {
    return false
  }
}

/**
 * Вызывает вибрацию через Web Vibration API
 */
const vibrateWeb = (pattern: number | readonly number[]): boolean => {
  if (!isWebVibrationSupported()) {
    return false
  }

  try {
    if (typeof pattern === 'number') {
      return navigator.vibrate(pattern)
    }

    return navigator.vibrate([...pattern])
  } catch {
    return false
  }
}

/**
 * Вызывает вибрацию с заданным паттерном
 * @param pattern - Паттерн вибрации (число или массив чисел)
 * @returns Promise<boolean> - true если вибрация была вызвана, false если не поддерживается
 */
export const vibrate = async (pattern: number | readonly number[]): Promise<boolean> => {
  if (isTelegramHapticSupported()) {
    const telegramResult = vibrateTelegram({
      pattern,
      telegramStyle: 'light',
      description: 'Стандартная тактильная отдача'
    })
    if (telegramResult) {
      return true
    }
  }

  return vibrateWeb(pattern)
}

/**
 * Вызывает вибрацию по предустановленному паттерну
 * @param patternName - Название паттерна из VIBRATION_PATTERNS
 * @returns Promise<boolean>
 */
export const vibratePattern = async (patternName: keyof typeof VIBRATION_PATTERNS): Promise<boolean> => {
  const pattern = VIBRATION_PATTERNS[patternName]
  if (!pattern) {
    console.warn(`Паттерн вибрации "${patternName}" не найден`)
    return false
  }

  if (isTelegramHapticSupported()) {
    const telegramResult = vibrateTelegram(pattern)
    if (telegramResult) {
      return true
    }
  }

  return vibrateWeb(pattern.pattern)
}

/**
 * Останавливает текущую вибрацию
 * @returns boolean
 */
export const stopVibration = (): boolean => {
  if (isWebVibrationSupported()) {
    return vibrateWeb(0)
  }

  // В Telegram WebApp нет явного stop API, считаем no-op успешным.
  if (isTelegramWebApp()) {
    return true
  }

  return false
}

/**
 * Composable для использования вибрации в компонентах Vue
 */
export const useVibration = () => {
  return {
    vibrate,
    vibratePattern,
    stopVibration,
    isVibrationSupported,
    isTelegramHapticSupported,
    isWebVibrationSupported,
    getVibrationChannel,
    isTelegramWebApp,
    VIBRATION_PATTERNS
  }
}
